import axios from 'axios';
import { MessagePlugin } from 'tdesign-vue-next';
import router from '@/router';
import { getActiveActionEvent } from '@/utils/audit-context';

// 创建axios实例
const request = axios.create({
  // 使用相对路径，通过代理访问API
  baseURL: '/api',
  // 以下是原始配置，现已注释
  // baseURL: 'http://127.0.0.1:8001/api/v1',
  // baseURL: 'http://api.eryu.asia:8001/api/v1',
  // baseURL: 'http://47.239.85.154:8001/api/v1',
  // baseURL: 'http://47.109.50.76:8001/api/v1',
  timeout: 600000 // 10 minutes
});

// 存储所有进行中的请求
const pendingRequests = new Map();

// 是否正在刷新token
let isRefreshing = false;

// 重试队列
let retryRequests = [];

// 生成请求的唯一key
const generateRequestKey = (config) => {
  const { method, url, params, data } = config;
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&');
};

// 添加请求到pending列表
const addPendingRequest = (config) => {
  if (config.skipPendingRequest) {
    return;
  }

  const requestKey = generateRequestKey(config);
  config.cancelToken = config.cancelToken || new axios.CancelToken((cancel) => {
    if (!pendingRequests.has(requestKey)) {
      pendingRequests.set(requestKey, cancel);
    }
  });
};

// 移除pending列表中的请求
const removePendingRequest = (config) => {
  if (config?.skipPendingRequest) {
    return;
  }

  const requestKey = generateRequestKey(config);
  if (pendingRequests.has(requestKey)) {
    pendingRequests.delete(requestKey);
  }
};

// 取消所有pending的请求
export const cancelAllPendingRequests = () => {
  pendingRequests.forEach((cancel) => {
    cancel('路由切换，取消请求');
  });
  pendingRequests.clear();
};

// 判断是否是取消的请求错误
export const isRequestCanceled = (error) => {
  return axios.isCancel(error);
};

// 获取用户信息
const getUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem('user_info') || '{}');
  } catch (error) {
    console.error('获取用户信息失败:', error);
    return {};
  }
};

// 是否超级管理员
const isSuperAdmin = () => {
  const userInfo = getUserInfo();
  return Number(userInfo?.role_id || 0) === 1;
};

// 清除登录信息
const clearAuthStorage = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_info');
  localStorage.removeItem('button_permissions');
  localStorage.removeItem('current_team_id');
  localStorage.removeItem('routes_initialized');
};

// 跳转登录
const redirectToLogin = (message = '登录已过期，请重新登录') => {
  clearAuthStorage();
  MessagePlugin.error(message);
  router.push('/login');
};

// 安全的错误处理函数 - 用于页面组件的 catch 块
export const handleRequestError = (error, defaultMessage = '操作失败') => {
  // 如果是取消的请求，静默处理
  if (axios.isCancel(error) || error?._isCanceled) {
    console.log('请求已取消，不显示错误');
    return false;
  }

  const message = error.response?.data?.msg || error.message || defaultMessage;
  MessagePlugin.error(message);
  return true;
};

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    console.log('发起请求:', config.url, config.method);

    // 在发起新请求前，移除已完成的请求
    removePendingRequest(config);

    // 添加新请求到pending列表
    addPendingRequest(config);

    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = token;
      console.log('请求携带 token');
    }

    // 将当前点击事件关联到该点击触发的业务请求。
    if (!config.skipActionAudit) {
      const actionEvent = getActiveActionEvent();
      if (actionEvent) {
        config.headers['X-Action-Event-ID'] = actionEvent.eventId;

        // HTTP 请求头只允许安全的 ASCII 字符，异常编码不能影响业务请求。
        if (/^[\x20-\x7E]+$/.test(actionEvent.actionKey)) {
          config.headers['X-Action-Key'] = actionEvent.actionKey;
        }
      }
    }

    // 只有超级管理员才自动添加 team_id
    // 管理员/普通用户不自动带 team_id，后端根据 token 中的 team_id 判断
    if ((config.method || 'get').toLowerCase() !== 'post') {
      const excludePaths = [
        '/user/login',
        '/user/register',
        '/user/refresh',
        '/user/profile'
      ];

      const shouldAddTeamId = !excludePaths.some((path) => config.url?.includes(path));

      if (shouldAddTeamId && isSuperAdmin()) {
        const currentTeamId = localStorage.getItem('current_team_id');

        if (currentTeamId) {
          config.params = config.params || {};

          if (!config.params.team_id) {
            config.params.team_id = currentTeamId;
            console.log('超级管理员请求自动添加 team_id:', currentTeamId);
          }
        }
      }
    }

    return config;
  },
  (error) => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    console.log('收到响应:', response.config.url, response.status, response.data);

    // 请求完成后，从pending列表中移除
    removePendingRequest(response.config);

    // 如果是blob类型的响应（如文件下载），直接返回原始响应
    if (response.config.responseType === 'blob') {
      return response;
    }

    const res = response.data;

    // 刷新令牌已过期，需要重新登录
    if (res.code === 48213) {
      redirectToLogin(res.msg || '登录已过期，请重新登录');
      return Promise.reject(new Error(res.msg || '刷新令牌已过期'));
    }

    return res;
  },
  async (error) => {
    // 如果是取消的请求，静默处理
    if (axios.isCancel(error)) {
      console.log('请求已取消，静默处理');
      return new Promise(() => {});
    }

    console.error('响应错误:', error.config?.url, error.response?.status, error.response?.data);

    // 请求失败后，从pending列表中移除
    if (error.config) {
      removePendingRequest(error.config);
    }

    // 审计上报失败时静默处理，不影响用户正在执行的业务操作。
    if (error.config?.silentError) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // access token 过期
    if (error.response?.data?.code === 48701 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          retryRequests.push(() => {
            originalRequest.headers.Authorization = localStorage.getItem('access_token');
            resolve(request(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');

        const response = await request.post('/user/refresh/', {
          refresh_token: refreshToken
        });

        if (response.code === 48213) {
          redirectToLogin(response.msg || '登录已过期，请重新登录');
          return Promise.reject(new Error(response.msg || '刷新令牌已过期'));
        }

        if (response.code === 200) {
          localStorage.setItem('access_token', response.data.access);

          retryRequests.forEach((cb) => cb());
          retryRequests = [];

          originalRequest.headers.Authorization = response.data.access;
          return request(originalRequest);
        }

        MessagePlugin.error(response.msg || '刷新登录状态失败，请重新登录');
        return Promise.reject(new Error(response.msg || '刷新登录状态失败'));
      } catch (refreshError) {
        if (refreshError.response?.data?.code === 48213) {
          redirectToLogin(refreshError.response?.data?.msg || '登录已过期，请重新登录');
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    MessagePlugin.error(error.response?.data?.msg || '请求失败');
    return Promise.reject(error);
  }
);

export default request;
